import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  DatePicker as AntDatePicker,
  message,
  Typography,
  Tag,
  Popconfirm,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  List,
  Avatar
} from 'antd';
import {
  ReloadOutlined,
  LoginOutlined,
  LogoutOutlined,
  SearchOutlined,
  PlusOutlined,
  DollarOutlined,
  EditOutlined,
  FileExcelOutlined,
  GiftOutlined,
  SmileOutlined,
  NotificationOutlined
} from '@ant-design/icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { attendanceAPI, adminAPI, paymentsAPI, measurementsAPI } from '../../utils/api';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useBiometric } from '../../contexts/BiometricContext';

import ConsumerEditModal from '../Consumers/ConsumerEditModal';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const Attendance = () => {
  const navigate = useNavigate();
  const [consumers, setConsumers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filteredConsumers, setFilteredConsumers] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({}); // Map of consumer_id -> attendance record
  const [measurementsMap, setMeasurementsMap] = useState({}); // Map of consumer_id -> measurement record for selected date
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({}); // Track loading state per consumer
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);
  const [editingDeposit, setEditingDeposit] = useState({}); // { [id]: inputValue }
  const [editingPending, setEditingPending] = useState({}); // { [id]: inputValue }
  const [searchText, setSearchText] = useState('');
  const [searchId, setSearchId] = useState('');
  const [filterCoach, setFilterCoach] = useState('all');
  const [coaches, setCoaches] = useState([]);

  // Get biometric status for auto-refresh
  const { scanStatus } = useBiometric();


  // Payment Modal State
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentForm] = Form.useForm();

  // Celebration Modal State
  const [celebrations, setCelebrations] = useState([]);
  const [celebrationModalVisible, setCelebrationModalVisible] = useState(false);

  useEffect(() => {
    // If date is today, default to 'all' to allow check-ins. 
    // If past date, default to 'present' for better report viewing as per request.
    const isToday = selectedDate.isSame(dayjs(), 'day');
    const newStatus = isToday ? 'all' : 'present';
    setFilterStatus(newStatus);

    fetchData().then(() => {
      // Data fetched, filter will be applied by the other useEffect
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, startDate, endDate]);

  // Effect to re-apply filters when consumers/attendanceMap changes (which happens after fetchData)
  // Also dependencies should include filterStatus so if status changes by date change, it triggers.
  useEffect(() => {
    applyFilters(searchText, searchId, filterStatus, filterCoach);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumers, attendanceMap, filterStatus, filterCoach]);

  // 🔄 Auto-refresh when biometric scan is successful
  useEffect(() => {
    if (scanStatus === 'success') {
      console.log('🔄 Biometric success detected, refreshing attendance list...');
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanStatus]);

  const handlePaymentSubmit = async (values) => {
    try {
      const paymentData = {
        ...values,
        payment_date: values.payment_date.format('YYYY-MM-DD'),
        period_start_date: values.period_start_date.format('YYYY-MM-DD'),
        period_end_date: values.period_end_date.format('YYYY-MM-DD')
      };

      await paymentsAPI.createPayment(paymentData);
      message.success('Payment recorded successfully');
      setPaymentModalVisible(false);
      paymentForm.resetFields();

      // Refresh data to update pending amounts
      fetchData();
    } catch (error) {
      message.error('Failed to record payment');
    }
  };

  const handleDepositEdit = async (record, addAmount) => {
    try {
      const amountToAdd = parseFloat(addAmount);
      if (isNaN(amountToAdd) || amountToAdd === 0) return;

      setActionLoading(prev => ({ ...prev, [record.id]: true }));

      // Always create a new payment record for additive behavior
      const paymentData = {
        consumer_id: record.id,
        amount: amountToAdd,
        payment_date: dayjs().format('YYYY-MM-DD'),
        period_start_date: dayjs().format('YYYY-MM-DD'),
        period_end_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        payment_type: record.plan_type === '3_day' ? 'daily' : 'monthly',
        status: 'completed',
        payment_method: 'cash',
        notes: `Quick deposit added from Attendance tab (Amount: ₹${amountToAdd})`
      };

      await paymentsAPI.createPayment(paymentData);
      message.success(`₹${amountToAdd.toLocaleString()} added to deposits`);
      fetchData();
    } catch (error) {
      console.error('Payment error:', error);
      message.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setActionLoading(prev => ({ ...prev, [record.id]: false }));
    }
  };

  const handlePendingEdit = async (consumerId, newTotalFee) => {
    try {
      const totalFeeVal = parseFloat(newTotalFee);
      if (isNaN(totalFeeVal) || totalFeeVal <= 0) return;

      setActionLoading(prev => ({ ...prev, [consumerId]: true }));
      await adminAPI.updateConsumer(consumerId, {
        total_amount: totalFeeVal
      });
      message.success('Total fee updated successfully');
      fetchData();
    } catch (error) {
      message.error('Failed to update total fee');
    } finally {
      setActionLoading(prev => ({ ...prev, [consumerId]: false }));
    }
  };

  const showPaymentModal = () => {
    paymentForm.resetFields();
    paymentForm.setFieldsValue({
      payment_type: 'monthly',
      status: 'completed',
      payment_method: 'cash'
    });
    setPaymentModalVisible(true);
  };

  // Consumer Modal Logic
  // Consumer Modal Logic
  const [consumerModalVisible, setConsumerModalVisible] = useState(false);

  const showConsumerModal = () => {
    setConsumerModalVisible(true);
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch all consumers
      const consumersResponse = await adminAPI.getConsumers({
        is_active: true,
        limit: 1000,
        // Include plan_type if possible (it's returned by the backend already)
      });
      const consumersList = consumersResponse.data.consumers || [];
      setConsumers(consumersList);
      setFilteredConsumers(consumersList);

      // Fetch coaches list for filtering
      const coachesResponse = await adminAPI.getCoaches();
      setCoaches(coachesResponse.data.coaches || []);

      // Fetch attendance
      const attendanceParams = {
        limit: 1000
      };

      if (startDate && endDate) {
        attendanceParams.start_date = dayjs(startDate).format('YYYY-MM-DD');
        attendanceParams.end_date = dayjs(endDate).format('YYYY-MM-DD');
      } else {
        attendanceParams.date = selectedDate.format('YYYY-MM-DD');
      }

      const attendanceResponse = await attendanceAPI.getAttendance(attendanceParams);
      const attendanceList = attendanceResponse.data.attendance || [];

      // Create a map of consumer_id -> attendance record
      // If range is selected, we might have multiple records per consumer. 
      // The current UI seems designed for single-day view (showing check-in time for 'today').
      // However, the backend returns a list. If we are in range mode, the map keying by consumer_id 
      // will overwrite previous days, showing only the latest record for that consumer.
      // This is acceptable behavior if the user understands "Attendance" view is primarily for checking current status.
      // Or we accept that for range view, the "Check In" column might show the LATEST check-in in that range.
      const map = {};
      attendanceList.forEach(record => {
        // In range mode, we might want to collect all attendance or just the latest?
        // Let's stick to the latest for the map which drives the main table display.
        // Or if the backend returns ordered by date DESC, we process them such that map ends up with latest?
        // Backend text says: ORDER BY a.date DESC
        // So the first record encountered for a consumer is the latest.
        if (!map[record.consumer_id]) {
          map[record.consumer_id] = record;
        }
      });
      setAttendanceMap(map);

      // Fetch health measurements
      const measurementParams = {
        limit: 1000
      };
      if (startDate && endDate) {
        measurementParams.start_date = dayjs(startDate).format('YYYY-MM-DD');
        measurementParams.end_date = dayjs(endDate).format('YYYY-MM-DD');
      } else {
        measurementParams.start_date = selectedDate.format('YYYY-MM-DD');
        measurementParams.end_date = selectedDate.format('YYYY-MM-DD');
      }

      const measurementsResponse = await measurementsAPI.getMeasurements(measurementParams);
      const measurementsList = measurementsResponse.data.measurements || [];
      const mMap = {};
      measurementsList.forEach(record => {
        mMap[record.consumer_id] = record;
      });
      setMeasurementsMap(mMap);
    } catch (error) {
      console.error('Fetch data error:', error);
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (consumerId) => {
    try {
      setActionLoading(prev => ({ ...prev, [consumerId]: true }));
      await attendanceAPI.checkIn({
        consumer_id: consumerId,
        date: selectedDate.format('YYYY-MM-DD')
      });
      message.success('Check-in recorded successfully');
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to record check-in';
      message.error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [consumerId]: false }));
    }
  };

  const handleCheckOut = async (consumerId) => {
    try {
      setActionLoading(prev => ({ ...prev, [consumerId]: true }));
      await attendanceAPI.checkOut({
        consumer_id: consumerId,
        date: selectedDate.format('YYYY-MM-DD')
      });
      message.success('Check-out recorded successfully');
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to record check-out';
      message.error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [consumerId]: false }));
    }
  };

  const handleUpgradeMembership = async (consumerId) => {
    try {
      setActionLoading(prev => ({ ...prev, [consumerId]: true }));
      await adminAPI.updateConsumer(consumerId, {
        plan_type: 'membership'
      });
      message.success('Consumer upgraded to Membership successfully');
      fetchData();
    } catch (error) {
      message.error('Failed to upgrade to Membership');
    } finally {
      setActionLoading(prev => ({ ...prev, [consumerId]: false }));
    }
  };

  const handleUpdateManualCount = async (consumerId, count) => {
    try {
      // Strip 'D' or 'd' if present (e.g., "D3" -> "3")
      const cleanCount = typeof count === 'string' ? count.replace(/[Dd]/g, '') : count;

      setActionLoading(prev => ({ ...prev, [consumerId]: true }));
      await adminAPI.updateConsumer(consumerId, {
        manual_attendance_count: cleanCount === null || cleanCount === '' ? null : parseInt(cleanCount)
      });
      message.success('Attendance count updated');
      fetchData();
    } catch (error) {
      message.error('Failed to update attendance count');
    } finally {
      setActionLoading(prev => ({ ...prev, [consumerId]: false }));
    }
  };

  const handleUpdateWeight = async (consumerId, weight) => {
    try {
      if (weight === null || weight === '') return;

      const parsedWeight = parseFloat(weight);
      if (isNaN(parsedWeight)) {
        message.error('Please enter a valid weight');
        return;
      }

      setActionLoading(prev => ({ ...prev, [consumerId]: true }));
      await measurementsAPI.quickUpdateWeight({
        consumer_id: consumerId,
        weight: parsedWeight,
        date: selectedDate.format('YYYY-MM-DD')
      });
      message.success('Weight updated');
      fetchData();
    } catch (error) {
      console.error('Update weight error:', error);
      message.error('Failed to update weight');
    } finally {
      setActionLoading(prev => ({ ...prev, [consumerId]: false }));
    }
  };

  const fetchCelebrations = async () => {
    try {
      console.log('🎉 Fetching celebrations from attendance page...');
      const response = await adminAPI.getCelebrations();
      console.log('📊 Celebrations response:', response.data);
      const celebrationsList = response.data.celebrations || [];
      console.log('🎂 Celebrations list:', celebrationsList);
      setCelebrations(celebrationsList);
      if (celebrationsList.length > 0) {
        console.log('✅ Opening celebration modal!');
        setCelebrationModalVisible(true);
      } else {
        console.log('ℹ️ No celebrations today');
      }
    } catch (error) {
      console.error('❌ Failed to fetch celebrations:', error);
    }
  };

  const renderCelebrationIcon = (type) => {
    return type === 'birthday' ? <SmileOutlined style={{ fontSize: '40px', color: '#ff4d4f' }} /> : <GiftOutlined style={{ fontSize: '40px', color: '#1890ff' }} />;
  };



  const handleTextSearch = (value) => {
    setSearchText(value);
    applyFilters(value, searchId, filterStatus, filterCoach);
  };

  const handleIdSearch = (value) => {
    setSearchId(value);
    applyFilters(searchText, value, filterStatus, filterCoach);
  };

  const handleStatusChange = (value) => {
    setFilterStatus(value);
    applyFilters(searchText, searchId, value, filterCoach);
  };

  const handleCoachChange = (value) => {
    setFilterCoach(value);
    applyFilters(searchText, searchId, filterStatus, value);
  };

  const applyFilters = (text, id, status, coach) => {
    const textLower = (text || '').toLowerCase().trim();
    const idLower = (id || '').toLowerCase().trim();
    const cleanId = idLower.startsWith('#') ? idLower.slice(1) : idLower;
    const currentStatus = status || 'all';
    const currentCoach = coach || 'all';

    const filtered = consumers.filter(consumer => {
      // 1. Status Filter
      if (currentStatus === 'present') {
        if (!attendanceMap[consumer.id]?.check_in_time) return false;
      } else if (currentStatus === 'absent') {
        if (attendanceMap[consumer.id]?.check_in_time) return false;
      }

      // 2. Coach Filter
      if (currentCoach !== 'all') {
        if (consumer.coach_name !== currentCoach) return false;
      }

      // 3. ID Filter (Exact or partial match if provided)
      if (cleanId) {
        const centerId = (consumer.center_consumer_id || '').toString();
        if (centerId !== cleanId && !centerId.startsWith(cleanId)) return false;
      }

      // 3. Text Filter (Name, Email, Phone)
      if (textLower) {
        const name = (consumer.name || '').toLowerCase();
        const email = (consumer.email || '').toLowerCase();
        const phone = (consumer.phone || '').toLowerCase();

        const matchesText = name.includes(textLower) ||
          email.includes(textLower) ||
          phone.includes(textLower);

        if (!matchesText) return false;
      }

      return true;
    });
    setFilteredConsumers(filtered);
  };

  const handleExportExcel = () => {
    try {
      // Prepare data for export
      const dataToExport = filteredConsumers.map(consumer => {
        const attendance = attendanceMap[consumer.id];

        let status = 'Not Present';
        if (attendance?.check_out_time) status = 'Completed';
        else if (attendance?.check_in_time) status = 'Checked In';

        let duration = '-';
        if (attendance?.hours_spent) {
          duration = `${Math.round(attendance.hours_spent * 100) / 100}h`;
        } else if (attendance?.check_in_time && !attendance?.check_out_time) {
          duration = 'In Progress';
        }

        return {
          'ID': consumer.center_consumer_id,
          'Consumer Name': consumer.name,
          'Coach Name': consumer.coach_name || '-',
          'Added Via': consumer.added_via === 'prime_time' ? 'Prime Time' : consumer.added_via === 'physical' ? 'Physical' : '-',
          'Weight (kg)': measurementsMap[consumer.id]?.weight || '-',
          'Email': consumer.email,
          'Total Present': consumer.total_days_present || 0,
          'Plan': consumer.plan_type ? consumer.plan_type.replace('_', ' ').toUpperCase() : 'MEMBERSHIP',
          'Deposited Amount': consumer.total_paid || 0,
          'Pending Amount': consumer.pending_amount !== undefined ? consumer.pending_amount : 7500,
          'Payment Method': consumer.latest_payment_method ? consumer.latest_payment_method.toUpperCase() : '-',
          'Phone': consumer.phone || '-',
          'Check In': attendance?.check_in_time ? dayjs(attendance.check_in_time).format('HH:mm A') : '-',
          'Check Out': attendance?.check_out_time ? dayjs(attendance.check_out_time).format('HH:mm A') : '-',
          'Duration': duration,
          'Status': status,
          'Last Check-in': consumer.last_check_in ? dayjs(consumer.last_check_in).format('YYYY-MM-DD HH:mm A') : 'NEVER',
          'Date': attendance?.date ? dayjs(attendance.date).format('YYYY-MM-DD') : selectedDate.format('YYYY-MM-DD')
        };
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);

      // Adjust column widths
      const wscols = [
        { wch: 8 },  // ID
        { wch: 20 }, // Consumer Name
        { wch: 15 }, // Coach Name
        { wch: 12 }, // Added Via
        { wch: 10 }, // Weight (kg)
        { wch: 25 }, // Email
        { wch: 15 }, // Total Present
        { wch: 15 }, // Plan
        { wch: 15 }, // Deposited Amount
        { wch: 15 }, // Pending Amount
        { wch: 15 }, // Payment Method
        { wch: 15 }, // Phone
        { wch: 10 }, // Check In
        { wch: 10 }, // Check Out
        { wch: 10 }, // Duration
        { wch: 15 }, // Status
        { wch: 18 }, // Last Check-in
        { wch: 12 }, // Date
      ];
      worksheet['!cols'] = wscols;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');

      // Generate buffer and save file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

      const fileName = `Attendance_${selectedDate.format('YYYY-MM-DD')}.xlsx`;
      saveAs(data, fileName);

      message.success('Attendance exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export attendance');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'center_consumer_id',
      key: 'center_consumer_id',
      width: 70,
      sorter: (a, b) => a.center_consumer_id - b.center_consumer_id,
      render: (id) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{id}</span>
    },
    {
      title: 'Consumer Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Coach',
      dataIndex: 'coach_name',
      key: 'coach_name',
      render: (coach) => <span style={{ fontStyle: 'italic', color: '#666' }}>{coach || '-'}</span>
    },
    {
      title: 'Added Via',
      dataIndex: 'added_via',
      key: 'added_via',
      render: (via) => {
        if (!via) return <span style={{ color: '#bbb' }}>-</span>;
        return (
          <Tag color={via === 'prime_time' ? 'purple' : 'orange'}>
            {via === 'prime_time' ? 'Prime Time' : 'Physical'}
          </Tag>
        );
      }
    },
    {
      title: 'Plan',
      dataIndex: 'plan_type',
      key: 'plan_type',
      render: (type) => {
        let color = 'gold';
        let label = 'Membership';
        if (type === 'sample') { color = 'purple'; label = 'Sample User'; }
        if (type === '3_day') { color = 'cyan'; label = '3 Day Plan'; }
        return <Tag color={color}>{label.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Check In',
      key: 'check_in',
      sorter: (a, b) => {
        const timeA = attendanceMap[a.id]?.check_in_time || '';
        const timeB = attendanceMap[b.id]?.check_in_time || '';
        if (!timeA && !timeB) return 0;
        if (!timeA) return -1;
        if (!timeB) return 1;
        return timeA.localeCompare(timeB);
      },
      render: (_, record) => {
        const attendance = attendanceMap[record.id];
        if (attendance?.check_in_time) {
          return dayjs(attendance.check_in_time).format('HH:mm A');
        }
        return (
          <Popconfirm
            title="Record check-in?"
            onConfirm={() => handleCheckIn(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              icon={<LoginOutlined />}
              size="small"
              loading={actionLoading[record.id]}
            >
              Check In
            </Button>
          </Popconfirm>
        );
      },
    },
    {
      title: 'Total Present',
      key: 'days_present',
      render: (_, record) => {
        const count = record.total_days_present || 0;
        const displayValue = record.plan_type === '3_day' ? `D${count}` : count;

        return (
          <Space>
            <Typography.Text
              editable={{
                onChange: (val) => handleUpdateManualCount(record.id, val),
                tooltip: 'Click to edit manual count',
              }}
              style={{ margin: 0 }}
            >
              <Tag color={record.plan_type === '3_day' ? 'cyan' : 'blue'}>
                {displayValue}
              </Tag>
            </Typography.Text>

            {record.plan_type === '3_day' && count >= 3 && (
              <Popconfirm
                title="Upgrade this user to Membership?"
                onConfirm={() => handleUpgradeMembership(record.id)}
                okText="Upgrade"
                cancelText="Cancel"
              >
                <Button type="primary" size="small" style={{ fontSize: '10px' }}>
                  MEMBERSHIP
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Deposited Amount',
      dataIndex: 'total_paid',
      key: 'total_paid',
      render: (amount, record) => {
        const isPaid = (record.pending_amount !== undefined ? record.pending_amount : (record.total_amount || 7500)) <= 0;
        const isEditing = editingDeposit[record.id] !== undefined;
        if (isEditing) {
          return (
            <Input
              autoFocus
              type="number"
              min="0"
              size="small"
              style={{ width: 90 }}
              defaultValue=""
              placeholder="Amount to add"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.dataset.submitted = '1';
                  const val = parseFloat(e.target.value);
                  setEditingDeposit(prev => { const n = { ...prev }; delete n[record.id]; return n; });
                  if (!isNaN(val) && val > 0) handleDepositEdit(record, val);
                }
                if (e.key === 'Escape') {
                  e.target.dataset.submitted = '1';
                  setEditingDeposit(prev => { const n = { ...prev }; delete n[record.id]; return n; });
                }
              }}
              onBlur={(e) => {
                if (e.target.dataset.submitted) return; // prevent double-submit after Enter
                const val = parseFloat(e.target.value);
                setEditingDeposit(prev => { const n = { ...prev }; delete n[record.id]; return n; });
                if (!isNaN(val) && val > 0) handleDepositEdit(record, val);
              }}
            />
          );
        }
        return (
          <Space size={4}>
            <Tag color="success" style={{ margin: 0 }}>
              {isPaid ? 'Paid: ' : ''}₹{(amount || 0).toLocaleString()}
            </Tag>
            <Button
              type="text" size="small"
              icon={<PlusOutlined style={{ color: '#52c41a', fontSize: '11px' }} />}
              onClick={() => setEditingDeposit(prev => ({ ...prev, [record.id]: null }))}
              title="Add deposit"
            />
          </Space>
        );
      },
    },
    {
      title: 'Pending Amount',
      dataIndex: 'pending_amount',
      key: 'pending_amount',
      render: (amount, record) => {
        const val = amount !== undefined ? amount : (record.total_amount || 7500);
        if (val <= 0) {
          return <Tag color="success">Paid</Tag>;
        }
        const isEditing = editingPending[record.id] !== undefined;
        if (isEditing) {
          return (
            <Input
              autoFocus
              type="number"
              min="0"
              size="small"
              style={{ width: 90 }}
              defaultValue=""
              placeholder="Total fee"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.dataset.submitted = '1';
                  const v = parseFloat(e.target.value);
                  setEditingPending(prev => { const n = { ...prev }; delete n[record.id]; return n; });
                  if (!isNaN(v) && v > 0) handlePendingEdit(record.id, v);
                }
                if (e.key === 'Escape') {
                  e.target.dataset.submitted = '1';
                  setEditingPending(prev => { const n = { ...prev }; delete n[record.id]; return n; });
                }
              }}
              onBlur={(e) => {
                if (e.target.dataset.submitted) return; // prevent double-submit after Enter
                const v = parseFloat(e.target.value);
                setEditingPending(prev => { const n = { ...prev }; delete n[record.id]; return n; });
                if (!isNaN(v) && v > 0) handlePendingEdit(record.id, v);
              }}
            />
          );
        }
        return (
          <Space size={4}>
            <Tag color="error" style={{ margin: 0 }}>₹{val.toLocaleString()}</Tag>
            <Button
              type="text" size="small"
              icon={<EditOutlined style={{ color: '#fa8c16', fontSize: '11px' }} />}
              onClick={() => setEditingPending(prev => ({ ...prev, [record.id]: val }))}
              title="Edit pending amount"
            />
          </Space>
        );
      }
    },
    {
      title: 'Payment Method',
      dataIndex: 'latest_payment_method',
      key: 'latest_payment_method',
      render: (method) => method ? <Tag>{method.toUpperCase()}</Tag> : <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: 'Weight (kg)',
      key: 'weight',
      width: 100,
      render: (_, record) => {
        const measurement = measurementsMap[record.id];
        const weight = measurement?.weight;

        return (
          <InputNumber
            placeholder="kg"
            defaultValue={weight}
            key={`${record.id}-${weight}`}
            onBlur={(e) => {
              const val = e.target.value;
              if (val !== '' && val !== String(weight)) {
                handleUpdateWeight(record.id, val);
              }
            }}
            onPressEnter={(e) => handleUpdateWeight(record.id, e.target.value)}
            style={{ width: '80px' }}
            size="small"
            step={0.1}
            min={0}
          />
        );
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '-',
    },
    {
      title: 'Duration',
      key: 'duration',
      sorter: (a, b) => (attendanceMap[a.id]?.hours_spent || 0) - (attendanceMap[b.id]?.hours_spent || 0),
      render: (_, record) => {
        const attendance = attendanceMap[record.id];
        if (attendance?.hours_spent) {
          return `${Math.round(attendance.hours_spent * 100) / 100}h`;
        }
        if (attendance?.check_in_time && !attendance?.check_out_time) {
          return <Tag color="orange">In Progress</Tag>;
        }
        return '-';
      },
    },
    {
      title: 'Status',
      key: 'status',
      sorter: (a, b) => {
        const getStatusWeight = (id) => {
          const attendance = attendanceMap[id];
          if (attendance?.check_out_time) return 3; // Completed
          if (attendance?.check_in_time) return 2; // Checked In
          return 1; // Not Present
        };
        return getStatusWeight(a.id) - getStatusWeight(b.id);
      },
      render: (_, record) => {
        const attendance = attendanceMap[record.id];
        if (attendance?.check_out_time) {
          return <Tag color="green">Completed</Tag>;
        }
        if (attendance?.check_in_time) {
          return <Tag color="blue">Checked In</Tag>;
        }
        return <Tag color="default">Not Present</Tag>;
      },
    },
  ];

  return (
    <div>
      {/* Celebration Modal */}
      <Modal
        title={
          <span style={{ fontSize: '20px' }}>
            <GiftOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
            Today's Celebrations! 🎉
          </span>
        }
        open={celebrationModalVisible}
        onCancel={() => setCelebrationModalVisible(false)}
        footer={[
          <Button
            key="send"
            type="primary"
            icon={<GiftOutlined />}
            onClick={async () => {
              try {
                setActionLoading(prev => ({ ...prev, sending_wishes: true }));
                const response = await adminAPI.sendCelebrationEmails();
                const { sent, failed, errors } = response.data;

                if (failed > 0) {
                  message.warning(`${sent} sent, ${failed} failed. Check console for details.`);
                  console.error('Failed emails:', errors);
                } else {
                  message.success(`All ${sent} wishes sent successfully!`);
                }
              } catch (error) {
                message.error('Failed to send wishes');
              } finally {
                setActionLoading(prev => ({ ...prev, sending_wishes: false }));
              }
            }}
            loading={actionLoading.sending_wishes}
            style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', marginRight: 8 }}
          >
            Send Wishes
          </Button>,
          <Button key="close" onClick={() => setCelebrationModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <List
          itemLayout="horizontal"
          dataSource={celebrations}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <Avatar
                    size={64}
                    icon={renderCelebrationIcon(item.celebration_type)}
                    style={{
                      backgroundColor: item.celebration_type === 'birthday' ? '#fff1f0' : '#e6f7ff',
                      border: `2px solid ${item.celebration_type === 'birthday' ? '#ff4d4f' : '#1890ff'}`
                    }}
                  />
                }
                title={
                  <span style={{ fontSize: '16px', fontWeight: 500 }}>
                    {item.name}
                    {item.celebration_type === 'birthday' && (
                      <Tag color="red" style={{ marginLeft: '8px' }}>
                        🎂 {item.years} years
                      </Tag>
                    )}
                    {item.celebration_type === 'anniversary' && (
                      <Tag color="blue" style={{ marginLeft: '8px' }}>
                        💍 {item.years} years
                      </Tag>
                    )}
                  </span>
                }
                description={
                  <div>
                    <div style={{ marginTop: '4px' }}>
                      {item.celebration_type === 'birthday' ? '🎈 Happy Birthday!' : '💐 Happy Anniversary!'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      {item.email} • {item.phone}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col flex="none">
          <Title level={5} style={{ margin: 0, fontSize: '18px', whiteSpace: 'nowrap' }}>
            Attendance Management
          </Title>
        </Col>
        <Col>
          <Space size="middle" wrap>
            <Button
              type="primary"
              icon={<DollarOutlined />}
              onClick={showPaymentModal}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              Record Payment
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showConsumerModal}
            >
              Add Consumer
            </Button>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                if (!date) {
                  setEndDate(null);
                  setSelectedDate(dayjs()); // Reset to today if cleared
                } else {
                  setSelectedDate(dayjs(date)); // Keep selectedDate in sync with start for single-day logic
                }
                if (date && endDate && date > endDate) setEndDate(null);
              }}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="Start Date"
              isClearable={false}
              className="ant-input"
              style={{ width: 130, height: 32 }}
              dateFormat="yyyy-MM-dd"
            />
            {startDate && (
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="End Date"
                isClearable
                className="ant-input"
                style={{ width: 130, height: 32 }}
                dateFormat="yyyy-MM-dd"
              />
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{ backgroundColor: '#217346', borderColor: '#217346', color: 'white' }}
            >
              Export Excel
            </Button>
            <Button
              icon={<GiftOutlined />}
              onClick={() => {
                fetchCelebrations();
                message.info('Checking for celebrations...');
              }}
            >
              Check Celebrations
            </Button>
            <Button
              icon={<NotificationOutlined />}
              onClick={async () => {
                try {
                  setActionLoading(prev => ({ ...prev, checking_absences: true }));
                  const response = await attendanceAPI.checkAbsences();
                  const { total_absent, emails_sent, emails_failed } = response.data;
                  if (total_absent === 0) {
                    message.success("Everyone is present today!");
                  } else {
                    message.success(`Found ${total_absent} absentees. ${emails_sent} notified, ${emails_failed} failed.`);
                  }
                } catch (error) {
                  message.error('Failed to notify absentees');
                } finally {
                  setActionLoading(prev => ({ ...prev, checking_absences: false }));
                }
              }}
              loading={actionLoading.checking_absences}
            >
              Notify Absentees
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={5}>
            <Search
              placeholder="Search by ID"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleIdSearch}
              onChange={(e) => handleIdSearch(e.target.value)}
              value={searchId}
              prefix={<Typography.Text type="secondary" style={{ marginRight: 4 }}>ID:</Typography.Text>}
            />
          </Col>
          <Col span={5}>
            <Select
              style={{ width: '100%' }}
              placeholder="Attendance Status"
              defaultValue="all"
              onChange={handleStatusChange}
              value={filterStatus}
            >
              <Option value="all">All Consumers</Option>
              <Option value="present">Present Only</Option>
              <Option value="absent">Absent Only</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by Coach"
              defaultValue="all"
              onChange={handleCoachChange}
              value={filterCoach}
              showSearch
            >
              <Option value="all">All Coaches</Option>
              {coaches.map(coach => (
                <Option key={coach.id} value={coach.name}>{coach.name}</Option>
              ))}
            </Select>
          </Col>
          <Col span={7}>
            <Search
              placeholder="Search by name, email, or phone"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleTextSearch}
              onChange={(e) => handleTextSearch(e.target.value)}
              value={searchText}
            />
          </Col>
          {(searchText || searchId) && (
            <Col span={8} style={{ textAlign: 'right', paddingTop: 8 }}>
              <Typography.Text type="secondary">
                Showing {filteredConsumers.length} of {consumers.length} consumers
              </Typography.Text>
            </Col>
          )}
        </Row>
        <Table
          columns={columns}
          dataSource={filteredConsumers}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} consumer${total !== 1 ? 's' : ''}`
          }}
        />
      </Card>

      <Modal
        title="Record Payment"
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handlePaymentSubmit}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="consumer_id"
                label="Consumer"
                rules={[{ required: true, message: 'Please select a consumer' }]}
              >
                <Select
                  placeholder="Select consumer"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {consumers.map(consumer => (
                    <Option key={consumer.id} value={consumer.id}>
                      {consumer.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="amount"
                label="Amount (₹)"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="Enter amount"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="payment_type"
                label="Payment Type"
                rules={[{ required: true, message: 'Please select payment type' }]}
              >
                <Select placeholder="Select payment type">
                  <Option value="daily">Daily</Option>
                  <Option value="monthly">Monthly</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="payment_method" label="Payment Method">
                <Select placeholder="Select payment method">
                  <Option value="cash">Cash</Option>
                  <Option value="card">Card</Option>
                  <Option value="online">Online</Option>
                  <Option value="bank_transfer">Bank Transfer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="payment_date"
                label="Payment Date"
                rules={[{ required: true, message: 'Please select payment date' }]}
              >
                <AntDatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="pending">Pending</Option>
                  <Option value="completed">Completed</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="period_start_date"
                label="Period Start Date"
                rules={[{ required: true, message: 'Please select period start date' }]}
              >
                <AntDatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="period_end_date"
                label="Period End Date"
                rules={[{ required: true, message: 'Please select period end date' }]}
              >
                <AntDatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="transaction_id" label="Transaction ID">
                <Input placeholder="Optional transaction ID" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setPaymentModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Record Payment
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <ConsumerEditModal
        visible={consumerModalVisible}
        onCancel={() => setConsumerModalVisible(false)}
        onSuccess={() => {
          setConsumerModalVisible(false);
          fetchData();
        }}
        consumer={null}
      />
    </div>
  );
};

export default Attendance;
